import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { FileText, Mail, Loader2, CheckCircle, AlertCircle, Zap, Shield } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAnalytics } from '@/hooks/useAnalytics';

interface FreeReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  occupation: string;
  area: string;
  soc: string;
  source?: string;
}

const FreeReportModal: React.FC<FreeReportModalProps> = ({
  isOpen,
  onClose,
  occupation,
  area,
  soc,
  source = 'unknown'
}) => {
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const analytics = useAnalytics();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsProcessing(true);

    try {
      const { data, error } = await supabase.functions.invoke('request-free-report', {
        body: {
          email: email.trim(),
          occupation,
          area,
          soc,
          honeypot
        }
      });

      if (error) {
        throw error;
      }

      if (data?.success) {
        setIsSent(true);
        analytics.trackFreeReportRequest(soc, area, source);
      } else {
        throw new Error(data?.error || 'Failed to send your report');
      }
    } catch (error) {
      console.error('Free report request error:', error);
      const message = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
      toast.error(message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset for next open after the close animation
    setTimeout(() => {
      setIsSent(false);
      setEmail('');
    }, 300);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Get Your Free Report
          </DialogTitle>
          <DialogDescription>
            A comprehensive salary and AI risk report for this occupation and location — free, delivered to your inbox
          </DialogDescription>
        </DialogHeader>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">{occupation}</CardTitle>
            <CardDescription>{area}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isSent ? (
              <div className="py-6 text-center space-y-3">
                <CheckCircle className="h-12 w-12 text-green-600 mx-auto" />
                <h4 className="font-semibold text-lg">Check your inbox!</h4>
                <p className="text-sm text-muted-foreground">
                  Your free report for <strong>{occupation}</strong> in <strong>{area}</strong> is
                  on its way to <strong>{email.trim()}</strong>. It usually arrives within a minute
                  — check your spam folder if you don't see it.
                </p>
                <Button onClick={handleClose} variant="outline" className="mt-2">
                  Done
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-2xl font-bold text-primary">Free</span>
                  <Badge variant="secondary">No payment required</Badge>
                </div>

                <div className="space-y-4">
                  <div className="p-3 sm:p-4 bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-lg">
                    <div className="flex items-start gap-3 mb-2">
                      <div className="p-2 bg-primary/10 rounded-lg shrink-0">
                        <Zap className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-sm mb-1 flex flex-wrap items-center gap-2">
                          AI Replacement Risk Analysis
                          <Badge variant="secondary" className="text-xs">Featured</Badge>
                        </h4>
                        <p className="text-xs text-muted-foreground mb-3">
                          Understand your career's future in the age of AI
                        </p>
                        <div className="grid grid-cols-3 gap-1.5 sm:gap-2 text-[11px] sm:text-xs">
                          <div className="flex items-center gap-1 min-w-0">
                            <AlertCircle className="h-3 w-3 text-primary shrink-0" />
                            <span className="whitespace-nowrap">Risk Score</span>
                          </div>
                          <div className="flex items-center gap-1 min-w-0">
                            <Shield className="h-3 w-3 text-primary shrink-0" />
                            <span className="whitespace-nowrap">Timeline</span>
                          </div>
                          <div className="flex items-center gap-1 min-w-0">
                            <CheckCircle className="h-3 w-3 text-primary shrink-0" />
                            <span className="whitespace-nowrap">Strategies</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-medium text-sm">Report also includes:</h4>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span>Complete salary breakdown (10th, 25th, 50th, 75th, 90th percentiles)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span>Employment statistics and job market data</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span>Historical trends and growth projections (15 years)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span>Industry breakdown and geographic comparisons</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <form
                  onSubmit={handleSubmit}
                  className="sticky bottom-0 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-3 pb-1 bg-background/95 backdrop-blur border-t space-y-3 relative z-10"
                >
                  <div className="absolute opacity-0 -z-10 h-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
                    <label htmlFor="free-report-hp">Website</label>
                    <input
                      id="free-report-hp"
                      name="website"
                      type="text"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isProcessing}
                  />
                  <Button
                    type="submit"
                    disabled={isProcessing}
                    className="w-full"
                    size="lg"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending your report...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Email Me My Free Report
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-center text-muted-foreground">
                    You'll also get our salary insights newsletter — unsubscribe anytime.
                  </p>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
};

export default FreeReportModal;
